import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllComments,
  selectAllImages,
  selectAllVideos,
  setUploadedContent,
} from '../../store/slices/uploadedContentSlice';
import { selectAllUrlParams } from '../../store/slices/urlParamsSlice';
import { idbGet, idbSet, isIdbAvailable } from '../../utils/idbStorage';

const getStorageKey = (eventCode) => `uploadedContent:${eventCode}`;

const normalizeStoredContent = (value) => ({
  images: Array.isArray(value?.images) ? value.images : [],
  videos: Array.isArray(value?.videos) ? value.videos : [],
  comments: Array.isArray(value?.comments) ? value.comments : [],
});

// Persistable URLs are data URLs / https URLs. Blob URLs are per-tab only and
// must never be persisted (they would dangle on reload).
const canPersistUrl = (url) => typeof url === 'string' && url.length > 0 && !url.startsWith('blob:');

const readPersisted = async (key) => {
  if (isIdbAvailable()) {
    const fromIdb = await idbGet(key);
    if (fromIdb !== undefined) return fromIdb;
  }
  // Migration / fallback: read legacy localStorage entry.
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Best-effort: migrate it to IndexedDB so future writes don't blow the
    // 5 MB localStorage quota. We don't await this so it doesn't block hydration.
    if (isIdbAvailable()) {
      idbSet(key, parsed).then((ok) => {
        if (ok) {
          try { window.localStorage.removeItem(key); } catch (_) { /* ignore */ }
        }
      });
    }
    return parsed;
  } catch (_) {
    return null;
  }
};

const writePersisted = async (key, value) => {
  if (isIdbAvailable()) {
    const ok = await idbSet(key, value);
    if (ok) return true;
  }
  // Last-resort fallback. Will fail silently on QuotaExceededError, which is
  // exactly the bug that lost guests' galleries before -- but it's still
  // better than nothing if IndexedDB is unavailable (e.g. some private modes).
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (_) {
    return false;
  }
};

const LocalContentSync = () => {
  const dispatch = useDispatch();
  const event = useSelector(selectAllUrlParams);
  const images = useSelector(selectAllImages);
  const videos = useSelector(selectAllVideos);
  const comments = useSelector(selectAllComments);
  const hydratedEventCodeRef = useRef(null);

  const eventCode = event?.eventCode;

  useEffect(() => {
    let cancelled = false;

    if (!eventCode) {
      dispatch(setUploadedContent({ images: [], videos: [], comments: [] }));
      hydratedEventCodeRef.current = null;
      return () => {
        cancelled = true;
      };
    }

    const hydrate = async () => {
      const persisted = await readPersisted(getStorageKey(eventCode));
      if (cancelled) return;
      dispatch(setUploadedContent(normalizeStoredContent(persisted)));
      hydratedEventCodeRef.current = eventCode;
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [dispatch, eventCode]);

  useEffect(() => {
    if (!eventCode || hydratedEventCodeRef.current !== eventCode) {
      return;
    }

    const payload = {
      images: images
        .filter((item) => item.eventCode === eventCode && canPersistUrl(item.url))
        .map((item) => ({
          id: item.id,
          type: item.type,
          url: item.url,
          name: item.name,
          size: item.size,
          uploadedAt: item.uploadedAt,
          eventCode: item.eventCode,
          guestName: item.guestName,
          guestPhone: item.guestPhone,
        })),
      videos: videos
        .filter((item) => item.eventCode === eventCode && canPersistUrl(item.url))
        .map((item) => ({
          id: item.id,
          type: item.type,
          url: item.url,
          name: item.name,
          size: item.size,
          uploadedAt: item.uploadedAt,
          eventCode: item.eventCode,
          guestName: item.guestName,
          guestPhone: item.guestPhone,
        })),
      comments: comments
        .filter((item) => item.eventCode === eventCode)
        .map((item) => ({
          id: item.id,
          type: item.type,
          text: item.text,
          uploadedAt: item.uploadedAt,
          eventCode: item.eventCode,
          guestName: item.guestName,
          guestPhone: item.guestPhone,
        })),
    };

    writePersisted(getStorageKey(eventCode), payload);
  }, [comments, eventCode, images, videos]);

  return null;
};

export default LocalContentSync;
